import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader, Building2, LogIn } from 'lucide-react';
import { BACKEND_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { clearUrlSecret, readUrlSecret } from '../lib/urlSecrets';
import { flag } from '../config';
import lazyAccountPage from '../components/site/auth/lazyAccountPage';

// SLOT_MODEL on: the redesigned page (site redesign Phase 6), loaded on demand.
const AcceptBusinessInviteSite = lazyAccountPage(
  'AcceptBusinessInviteSite',
  () => import(/* webpackChunkName: "account-pages" */ './account/AcceptBusinessInviteSite'),
);

/** /accept-business-invite: the redesigned page with SLOT_MODEL on, the legacy V5 page (unchanged) off. */
export default function AcceptBusinessInvite() {
  return flag('SLOT_MODEL') ? <AcceptBusinessInviteSite /> : <AcceptBusinessInviteLegacy />;
}

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    green: '#22c55e',
    red: '#ef4444',
    teal: '#14b8a6',
    orange: '#f97316',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

function AcceptBusinessInviteLegacy() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, openAuthModal, refreshUser } = useAuth();
  // In the URL, or kept for this page by the index.html URL secret guard (lib/urlSecrets).
  const [keptToken] = useState(() => readUrlSecret('token', { location }));
  
  const [status, setStatus] = useState('checking'); // 'checking', 'login_required', 'accepting', 'success', 'error'
  const [message, setMessage] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  
  useEffect(() => {
    const token = searchParams.get('token') || keptToken;
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link. No token provided.');
      return;
    }
    
    setInviteToken(token);
    
    // If user is authenticated, try to accept the invite
    if (isAuthenticated && user) {
      acceptInvite(token);
    } else {
      // Store token for after login
      sessionStorage.setItem('pending_business_invite_token', token);
      setStatus('login_required');
      setMessage('Please log in to accept this business invitation.');
    }
  }, [searchParams, isAuthenticated, user]);
  
  // Check for pending invite after login
  useEffect(() => {
    if (isAuthenticated && user) {
      const pendingToken = sessionStorage.getItem('pending_business_invite_token');
      if (pendingToken && status === 'login_required') {
        sessionStorage.removeItem('pending_business_invite_token');
        acceptInvite(pendingToken);
      }
    }
  }, [isAuthenticated, user, status]);
  
  const acceptInvite = async (token) => {
    setStatus('accepting');
    setMessage('Linking you to the business...');
    
    try {
      const authToken = localStorage.getItem('token');
      const sessionToken = localStorage.getItem('oauth_session_token');
      
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      } else if (sessionToken) {
        headers['X-OAuth-Session-Token'] = sessionToken;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/accept-business-invite`,
        { token },
        { headers }
      );
      
      if (response.data.success) {
        clearUrlSecret('token');
        setStatus('success');
        setBusinessName(response.data.business_name || 'the business');
        setMessage(response.data.message || 'You are now linked to this business!');
        
        // Refresh user data to get updated role/business_id
        await refreshUser();
        
        // Redirect to business dashboard after 3 seconds
        setTimeout(() => {
          navigate('/business/dashboard');
        }, 3000);
      }
    } catch (error) {
      console.error('Accept invite error:', error);
      setStatus('error');
      
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        if (errorDetail.includes('expired')) {
          setMessage('This invitation has expired. Please ask for a new invitation.');
        } else if (errorDetail.includes('different email')) {
          setMessage('This invitation was sent to a different email address. Please log in with the correct account.');
        } else if (errorDetail.includes('already has an owner')) {
          setMessage('This business already has an owner assigned.');
        } else {
          setMessage(errorDetail);
        }
      } else {
        setMessage('Failed to accept invitation. Please try again or contact support.');
      }
    }
  };
  
  const handleLogin = () => {
    // Store the token before opening login modal
    if (inviteToken) {
      sessionStorage.setItem('pending_business_invite_token', inviteToken);
    }
    openAuthModal('login');
  };
  
  const handleSignup = () => {
    // Store the token before opening signup modal
    if (inviteToken) {
      sessionStorage.setItem('pending_business_invite_token', inviteToken);
    }
    openAuthModal('signup');
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'checking':
      case 'accepting':
        return {
          icon: <Loader className="w-16 h-16 animate-spin" style={{ color: v5.colors.purple }} />,
          borderColor: v5.colors.purple,
          iconBg: 'rgba(139, 92, 246, 0.1)',
          title: status === 'checking' ? 'Checking Invitation' : 'Accepting Invitation',
          codeAccent: '// business_invite'
        };
      case 'login_required':
        return {
          icon: <LogIn className="w-16 h-16" style={{ color: v5.colors.orange }} />,
          borderColor: v5.colors.orange,
          iconBg: 'rgba(249, 115, 22, 0.1)',
          title: 'Login Required',
          codeAccent: '// authentication_required'
        };
      case 'success':
        return {
          icon: <Building2 className="w-16 h-16" style={{ color: v5.colors.green }} />,
          borderColor: v5.colors.green,
          iconBg: 'rgba(34, 197, 94, 0.1)',
          title: 'Welcome Aboard!',
          codeAccent: '// invite_accepted'
        };
      case 'error':
        return {
          icon: <XCircle className="w-16 h-16" style={{ color: v5.colors.red }} />,
          borderColor: v5.colors.red,
          iconBg: 'rgba(239, 68, 68, 0.1)',
          title: 'Invitation Error',
          codeAccent: '// invite_error'
        };
      default:
        return {};
    }
  };

  const config = getStatusConfig();
  
  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-20"
      style={{ backgroundColor: v5.colors.cream }}
      data-testid="accept-business-invite-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-xl p-8 md:p-12 max-w-md w-full text-center"
        style={{ 
          backgroundColor: v5.colors.white,
          border: `1px solid ${v5.colors.borderLight}`,
          boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
          borderLeft: `4px solid ${config.borderColor}`
        }}
      >
        {/* Code Accent */}
        <p 
          className="mb-4"
          style={{ 
            fontFamily: v5.fonts.mono,
            fontSize: '12px',
            color: v5.colors.textHint
          }}
        >
          {config.codeAccent}
        </p>

        {/* Loading States */}
        {(status === 'checking' || status === 'accepting') && (
          <div>
            <div 
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: config.iconBg }}
            >
              {config.icon}
            </div>
            <h2 
              className="text-2xl mb-4"
              style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
            >
              {config.title}
            </h2>
            <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}>
              {message || 'Please wait...'}
            </p>
          </div>
        )}
        
        {/* Login Required */}
        {status === 'login_required' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div 
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: config.iconBg }}
            >
              {config.icon}
            </div>
            <h2 
              className="text-2xl mb-4"
              style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
            >
              {config.title}
            </h2>
            <p 
              className="mb-6"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              {message}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleLogin}
                className="w-full px-8 py-3 rounded-xl font-semibold text-white transition-all hover:-translate-y-0.5"
                style={{ 
                  background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
                  fontFamily: v5.fonts.body,
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                }}
                data-testid="login-button"
              >
                Log In
              </button>
              
              <p 
                className="text-sm"
                style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
              >
                Don't have an account?{' '}
                <button 
                  onClick={handleSignup}
                  className="underline hover:no-underline"
                  style={{ color: v5.colors.purple }}
                  data-testid="signup-link"
                >
                  Sign up
                </button>
              </p>
            </div>
          </motion.div>
        )}
        
        {/* Success */}
        {status === 'success' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div 
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: config.iconBg }}
            >
              {config.icon}
            </div>
            <h2 
              className="text-2xl mb-4"
              style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
            >
              {config.title}
            </h2>
            <p 
              className="mb-2"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              {message}
            </p>
            <p 
              className="mb-6 font-semibold"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.purple }}
            >
              {businessName}
            </p>
            <p 
              className="text-sm"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textHint }}
            >
              Redirecting to your business dashboard...
            </p>
          </motion.div>
        )}
        
        {/* Error */}
        {status === 'error' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div 
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: config.iconBg }}
            >
              {config.icon}
            </div>
            <h2 
              className="text-2xl mb-4"
              style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
            >
              {config.title}
            </h2>
            <p 
              className="mb-6"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              {message}
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-all hover:-translate-y-0.5"
              style={{ 
                background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
                fontFamily: v5.fonts.body,
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
              }}
              data-testid="go-home-button"
            >
              Go to Homepage
            </button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
